import { useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '@/components/ui/Modal'
import { getCoupons, createCoupon, clearCoupons, toggleCoupon, incrementCouponUsage, deleteCoupon } from '@/services/mock/coupons'

function randomCode(len = 6) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let out = ''
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)]
  return out
}

export default function DiscountedCouponPage() {
  const [open, setOpen] = useState(false)
  const [coupons, setCoupons] = useState([])
  const [loading, setLoading] = useState(false)

  // form
  const [code, setCode] = useState('')
  const [auto, setAuto] = useState(true)
  const [minOrderValue, setMinOrderValue] = useState('')
  const [maxOrderValue, setMaxOrderValue] = useState('')
  const [discountType, setDiscountType] = useState('percent')
  const [discountValue, setDiscountValue] = useState('')
  const [expiry, setExpiry] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const list = await getCoupons()
      setCoupons(list)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const openCreate = () => {
    setError('')
    setCode(randomCode())
    setAuto(true)
    setMinOrderValue('')
    setMaxOrderValue('')
    setMaxUses('')
    setDescription('')
    setDiscountType('percent')
    setDiscountValue('')
    setExpiry('')
    setOpen(true)
  }

  const handleCreate = async () => {
    setError('')
    const finalCode = (auto && !code) ? randomCode() : code.trim().toUpperCase()
    if (!finalCode || finalCode.length < 4) return setError('Please provide a valid coupon code (min 4 chars)')
    const minVal = Number(minOrderValue || 0)
    const maxVal = maxOrderValue ? Number(maxOrderValue) : null
    const maxUsesVal = maxUses ? Number(maxUses) : null
    if (maxUses && (isNaN(maxUsesVal) || maxUsesVal < 1)) return setError('Max uses must be a positive integer or empty for unlimited')
    if (maxVal !== null && maxVal < minVal) return setError('Maximum order value cannot be less than minimum')
    if (!discountValue) return setError('Please provide a discount value')

    try {
      await createCoupon({ code: finalCode, minOrderValue: minVal, maxOrderValue: maxVal, discountType, discountValue: Number(discountValue), expiry, maxUses: maxUsesVal, description: description.trim() })
      await load()
      setOpen(false)
    } catch (e) {
      if (e.code === 'DUPLICATE') setError('Coupon code already exists')
      else {
        console.error(e)
        setError('Failed to create coupon')
      }
    }
  }

  const handleClearAll = async () => {
    if (!confirm('Clear all coupons from localStorage?')) return
    await clearCoupons()
    await load()
  }

  const toggleEnabled = async (id, enabled) => {
    try {
      await toggleCoupon(id, enabled)
      await load()
    } catch (e) {
      console.error(e)
    }
  }

  const simulateUse = async (id) => {
    try {
      await incrementCouponUsage(id)
      await load()
    } catch (e) {
      if (e.code === 'MAX_REACHED') {
        alert('Coupon has reached its maximum allowed uses')
      } else {
        console.error(e)
      }
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this coupon?')) return
    try {
      await deleteCoupon(id)
      await load()
    } catch (e) {
      console.error(e)
      alert('Failed to delete coupon')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Discounted Coupon</h1>
        <Breadcrumbs />
      </div>

      <section className="bg-card border p-4 rounded-xl">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Create and manage discounted coupons.</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={handleClearAll}>Clear All</Button>
            <Button onClick={openCreate}>Create Coupon</Button>
          </div>
        </div>

        <div className="mt-4">
          {loading ? <div>Loading...</div> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-2">Code</th>
                  <th className="p-2">Min</th>
                  <th className="p-2">Max</th>
                  <th className="p-2">Type</th>
                  <th className="p-2">Value</th>
                  <th className="p-2">Expiry</th>
                  <th className="p-2">Description</th>
                  <th className="p-2">Uses</th>
                  <th className="p-2">Enabled</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length === 0 ? (
                  <tr><td colSpan={9} className="p-3 text-muted-foreground">No coupons</td></tr>
                ) : coupons.map(c => (
                  <tr key={c.id} className="border-b">
                    <td className="p-2 font-medium">{c.code}</td>
                    <td className="p-2">{c.minOrderValue}</td>
                    <td className="p-2">{c.maxOrderValue ?? '-'}</td>
                    <td className="p-2">{c.discountType}</td>
                    <td className="p-2">{c.discountValue}</td>
                    <td className="p-2">{c.expiry || '-'}</td>
                    <td className="p-2">{c.description || '-'}</td>
                    <td className="p-2">{(c.uses || 0)}{c.maxUses ? ` / ${c.maxUses}` : ''}</td>
                    <td className="p-2">{c.enabled ? 'Enabled' : 'Disabled'}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <button className="px-2 py-1 rounded bg-muted text-sm" onClick={async (e) => { e.stopPropagation(); await toggleEnabled(c.id, !c.enabled) }}>{c.enabled ? 'Disable' : 'Enable'}</button>
                        <button className="px-2 py-1 rounded bg-muted text-sm" onClick={async (e) => { e.stopPropagation(); await simulateUse(c.id) }}>Simulate Use</button>
                        <button className="px-2 py-1 rounded bg-destructive text-sm" onClick={async (e) => { e.stopPropagation(); await handleDelete(c.id) }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <Modal open={open} onOpenChange={setOpen}>
        <ModalHeader onClose={() => setOpen(false)}>
          <ModalTitle>Create Coupon</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="text-sm block mb-1">Code</label>
              <div className="flex gap-2">
                <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="6 character code" />
                <Button variant="ghost" onClick={() => setCode(randomCode())}>Generate</Button>
                <label className="flex items-center gap-2 ml-2"><input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} /> Auto</label>
              </div>
                <label className="text-sm block mb-1">Description (optional)</label>
                <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description" />
              </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm block mb-1">Minimum order value</label>
                <Input value={minOrderValue} onChange={e => setMinOrderValue(e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className="text-sm block mb-1">Maximum order value (optional)</label>
                <Input value={maxOrderValue} onChange={e => setMaxOrderValue(e.target.value)} placeholder="optional" />
              </div>
            </div>

            <div>
              <label className="text-sm block mb-1">Max uses (optional)</label>
              <Input value={maxUses} onChange={e => setMaxUses(e.target.value)} placeholder="leave empty for unlimited" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm block mb-1">Discount type</label>
                <select value={discountType} onChange={e => setDiscountType(e.target.value)} className="input w-full">
                  <option value="percent">Percent (%)</option>
                  <option value="amount">Amount</option>
                </select>
              </div>
              <div>
                <label className="text-sm block mb-1">Discount value</label>
                <Input value={discountValue} onChange={e => setDiscountValue(e.target.value)} placeholder="e.g., 10" />
              </div>
            </div>

            <div>
              <label className="text-sm block mb-1">Expiry (optional)</label>
              <Input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
            </div>

            {error && <div className="text-sm text-destructive">{error}</div>}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate}>Create</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
